import type { AppData } from "../types";
import { requireSupabase } from "../lib/supabase";

type IdRow = { id: string };

function ids<T extends { id: string }>(rows: T[]): string[] {
  return rows.map((row) => row.id);
}

async function upsertRows(table: string, rows: Record<string, unknown>[]): Promise<void> {
  if (!rows.length) return;

  const db = requireSupabase();
  const { error } = await db.from(table).upsert(rows, { onConflict: "id" });

  if (error) {
    throw new Error(`${table}: ${error.message}`);
  }
}

async function deleteIds(table: string, rowIds: string[]): Promise<void> {
  if (!rowIds.length) return;

  const db = requireSupabase();
  const { error } = await db.from(table).delete().in("id", rowIds);

  if (error) {
    throw new Error(`${table}: ${error.message}`);
  }
}

async function listIdsByValue(
  table: string,
  column: string,
  value: string,
): Promise<string[]> {
  const db = requireSupabase();
  const { data, error } = await db.from(table).select("id").eq(column, value);

  if (error) {
    throw new Error(`${table}: ${error.message}`);
  }

  return ((data ?? []) as IdRow[]).map((row) => row.id);
}

async function listIdsByValues(
  table: string,
  column: string,
  values: string[],
): Promise<string[]> {
  if (!values.length) return [];

  const db = requireSupabase();
  const { data, error } = await db.from(table).select("id").in(column, values);

  if (error) {
    throw new Error(`${table}: ${error.message}`);
  }

  return ((data ?? []) as IdRow[]).map((row) => row.id);
}

function missing(remoteIds: string[], localIds: string[]): string[] {
  const keep = new Set(localIds);
  return remoteIds.filter((id) => !keep.has(id));
}

export async function syncAppDataToSupabase(
  data: AppData,
  workspaceId: string,
): Promise<void> {
  const worldRows = data.worlds.map((world) => ({
    id: world.id,
    workspace_id: workspaceId,
    name: world.name,
    description: world.description,
    theme: world.theme,
    magic_level: world.magicLevel,
    game_system: world.system,
    created_at: world.createdAt,
  }));

  const entityRows = data.entities.map((entity) => ({
    id: entity.id,
    world_id: entity.worldId,
    entity_type: entity.type,
    name: entity.name,
    description: entity.description,
    visibility: entity.visibility,
    tags: entity.tags,
    details: entity.details,
    created_at: entity.createdAt,
  }));

  const relationshipRows = data.relationships.map((relationship) => ({
    id: relationship.id,
    world_id: relationship.worldId,
    source_entity_id: relationship.sourceId,
    target_entity_id: relationship.targetId,
    relationship_type: relationship.type,
    description: relationship.description,
    is_secret: relationship.secret,
    created_at: relationship.createdAt,
  }));

  const campaignRows = data.campaigns.map((campaign) => ({
    id: campaign.id,
    world_id: campaign.worldId,
    name: campaign.name,
    description: campaign.description,
    created_at: campaign.createdAt,
  }));

  const campaignStateRows = data.campaignState.map((state) => ({
    id: state.id,
    campaign_id: state.campaignId,
    entity_id: state.entityId,
    discovery_state: state.discoveryState ?? null,
    reputation: state.reputation ?? null,
    disposition: state.disposition ?? null,
    status: state.status ?? null,
    notes: state.notes ?? null,
  }));

  const campaignEventRows = data.campaignEvents.map((event) => ({
    id: event.id,
    campaign_id: event.campaignId,
    event_type: event.type,
    entity_id: event.entityId ?? null,
    related_entity_id: event.relatedEntityId ?? null,
    session_number: event.sessionNumber ?? null,
    description: event.description,
    created_at: event.createdAt,
  }));

  const mapRows = data.maps.map((map) => ({
    id: map.id,
    world_id: map.worldId,
    name: map.name,
    map_type: map.mapType,
    linked_entity_id: map.linkedEntityId ?? null,
    gm_url: map.gmUrl ?? null,
    player_url: map.playerUrl ?? null,
    vtt_url: map.vttUrl ?? null,
    grid_type: map.gridType ?? null,
    grid_size: map.gridSize ?? null,
    columns: map.columns ?? null,
    rows: map.rows ?? null,
    notes: map.notes,
    created_at: map.createdAt,
  }));

  const itemDefinitionRows = data.itemDefinitions.map((item) => ({
    id: item.id,
    workspace_id: workspaceId,
    name: item.name,
    category: item.category,
    subtype: item.subtype ?? null,
    rarity: item.rarity ?? null,
    magical: item.magical,
    requires_attunement: item.requiresAttunement,
    attunement_requirements: item.attunementRequirements ?? null,
    consumable: item.consumable,
    charges: item.charges ?? null,
    weight: item.weight ?? null,
    value_gp: item.valueGp ?? null,
    source: item.source ?? null,
    rules_version: item.rulesVersion ?? null,
    summary: item.summary,
    mechanics: item.mechanics,
    lore: item.lore,
    gm_secrets: item.gmSecrets,
    image_url: item.imageUrl ?? null,
    tags: item.tags,
    source_type: item.sourceType ?? "homebrew",
    source_ruleset: item.sourceRuleset ?? null,
    source_resource_type: item.sourceResourceType ?? null,
    source_external_id: item.sourceExternalId ?? null,
    source_url: item.sourceUrl ?? null,
    imported_at: item.importedAt ?? null,
    created_at: item.createdAt,
    updated_at: item.updatedAt,
  }));

  const itemInstanceRows = data.itemInstances.map((item) => ({
    id: item.id,
    world_id: item.worldId,
    definition_id: item.definitionId,
    display_name: item.displayName ?? null,
    unique_item: item.uniqueItem,
    quantity: item.quantity,
    condition: item.condition,
    identified: item.identified,
    current_owner_entity_id: item.currentOwnerEntityId ?? null,
    current_holder_entity_id: item.currentHolderEntityId ?? null,
    current_location_entity_id: item.currentLocationEntityId ?? null,
    notes: item.notes,
    created_at: item.createdAt,
    updated_at: item.updatedAt,
  }));

  const itemMovementRows = data.itemMovements.map((movement) => ({
    id: movement.id,
    campaign_id: movement.campaignId ?? null,
    item_instance_id: movement.itemId,
    from_entity_id: movement.fromEntityId ?? null,
    to_entity_id: movement.toEntityId ?? null,
    movement_type: movement.movementType,
    notes: movement.notes,
    created_at: movement.createdAt,
  }));

  const creatureRows = data.creatureDefinitions.map((creature) => ({
    id: creature.id,
    workspace_id: workspaceId,
    name: creature.name,
    size: creature.size,
    creature_type: creature.creatureType,
    alignment: creature.alignment,
    armor_class: creature.armorClass ?? null,
    hit_points: creature.hitPoints ?? null,
    hit_dice: creature.hitDice,
    speed: creature.speed,
    abilities: creature.abilities,
    saves: creature.saves,
    skills: creature.skills,
    damage_vulnerabilities: creature.damageVulnerabilities,
    damage_resistances: creature.damageResistances,
    damage_immunities: creature.damageImmunities,
    condition_immunities: creature.conditionImmunities,
    senses: creature.senses,
    languages: creature.languages,
    challenge_rating: creature.challengeRating ?? null,
    xp: creature.xp ?? null,
    proficiency_bonus: creature.proficiencyBonus ?? null,
    traits: creature.traits,
    actions: creature.actions,
    bonus_actions: creature.bonusActions,
    reactions: creature.reactions,
    legendary_actions: creature.legendaryActions,
    mythic_actions: creature.mythicActions,
    spellcasting: creature.spellcasting,
    lore: creature.lore,
    gm_notes: creature.gmNotes,
    source_type: creature.sourceType,
    source_ruleset: creature.sourceRuleset ?? null,
    source_external_id: creature.sourceExternalId ?? null,
    source_url: creature.sourceUrl ?? null,
    imported_at: creature.importedAt ?? null,
    created_at: creature.createdAt,
    updated_at: creature.updatedAt,
  }));

  const currencyRows = data.currencies.map((currency) => ({
    id: currency.id,
    workspace_id: workspaceId,
    code: currency.code,
    name: currency.name,
    symbol: currency.symbol,
    base_value: currency.baseValue,
    weight_per_unit: currency.weightPerUnit ?? null,
    created_at: currency.createdAt,
    updated_at: currency.updatedAt,
  }));

  await upsertRows("worlds", worldRows);
  await upsertRows("entities", entityRows);
  await upsertRows("relationships", relationshipRows);
  await upsertRows("campaigns", campaignRows);
  await upsertRows("campaign_entity_state", campaignStateRows);
  await upsertRows("campaign_events", campaignEventRows);
  await upsertRows("maps", mapRows);
  await upsertRows("item_definitions", itemDefinitionRows);
  await upsertRows("item_instances", itemInstanceRows);
  await upsertRows("item_movements", itemMovementRows);
  await upsertRows("creature_definitions", creatureRows);
  await upsertRows("currencies", currencyRows);

  const remoteWorldIds = await listIdsByValue("worlds", "workspace_id", workspaceId);
  const allWorldIds = Array.from(new Set([...remoteWorldIds, ...ids(data.worlds)]));

  const remoteCampaignIds = await listIdsByValues("campaigns", "world_id", allWorldIds);
  const remoteItemInstanceIds = await listIdsByValues("item_instances", "world_id", allWorldIds);

  await deleteIds(
    "item_movements",
    missing(
      await listIdsByValues("item_movements", "item_instance_id", remoteItemInstanceIds),
      ids(data.itemMovements),
    ),
  );
  await deleteIds(
    "campaign_entity_state",
    missing(
      await listIdsByValues("campaign_entity_state", "campaign_id", remoteCampaignIds),
      ids(data.campaignState),
    ),
  );
  await deleteIds(
    "campaign_events",
    missing(
      await listIdsByValues("campaign_events", "campaign_id", remoteCampaignIds),
      ids(data.campaignEvents),
    ),
  );
  await deleteIds(
    "relationships",
    missing(
      await listIdsByValues("relationships", "world_id", allWorldIds),
      ids(data.relationships),
    ),
  );
  await deleteIds(
    "maps",
    missing(await listIdsByValues("maps", "world_id", allWorldIds), ids(data.maps)),
  );
  await deleteIds(
    "item_instances",
    missing(
      await listIdsByValues("item_instances", "world_id", allWorldIds),
      ids(data.itemInstances),
    ),
  );
  await deleteIds(
    "entities",
    missing(await listIdsByValues("entities", "world_id", allWorldIds), ids(data.entities)),
  );
  await deleteIds(
    "campaigns",
    missing(await listIdsByValues("campaigns", "world_id", allWorldIds), ids(data.campaigns)),
  );

  await deleteIds("worlds", missing(remoteWorldIds, ids(data.worlds)));
  await deleteIds(
    "item_definitions",
    missing(
      await listIdsByValue("item_definitions", "workspace_id", workspaceId),
      ids(data.itemDefinitions),
    ),
  );
  await deleteIds(
    "creature_definitions",
    missing(
      await listIdsByValue("creature_definitions", "workspace_id", workspaceId),
      ids(data.creatureDefinitions),
    ),
  );
  await deleteIds(
    "currencies",
    missing(
      await listIdsByValue("currencies", "workspace_id", workspaceId),
      ids(data.currencies),
    ),
  );
}
